import { computeRulerDistance, type RulerCoord } from '@/hooks/useRuler'
import {
  computeCircleTemplate,
  computeSquareTemplate,
  computeConeTemplate,
} from '@/utils/geometry'
import type { MeasurementShape } from '@/types'

export interface RulerOverlayProps {
  /** Origin in world space (post-inverse-transform). */
  start: RulerCoord | null
  /** Endpoint in world space. */
  end: RulerCoord | null
  /** Pixels per grid square. */
  gridSize: number
  /** Current viewport scale (PIXI stage scale). */
  viewportScale: number
  /** Current viewport translation x (PIXI stage position.x). */
  viewportX: number
  /** Current viewport translation y (PIXI stage position.y). */
  viewportY: number
  /** Width of the canvas DOM element in CSS pixels. */
  canvasWidth: number
  /** Height of the canvas DOM element in CSS pixels. */
  canvasHeight: number
  /** Whether the ruler line should currently be visible. */
  visible: boolean
  /**
   * Line/marker hue. Defaults to the local ruler's amber. Remote rulers pass
   * a per-user color so multiple simultaneous measurements are distinguishable.
   */
  color?: string
  /**
   * Optional name prefix on the distance label (e.g. a remote peer's display
   * name). Omitted for the local ruler.
   */
  label?: string
  /**
   * Which measurement template to render. `line` is the classic straight
   * ruler (default); cone/circle/square render the matching 5e area
   * template sized by the start->end drag. Remote rulers (broadcast,
   * follow-up on #90) default to `line` until the payload carries shape.
   */
  shape?: MeasurementShape
}

/**
 * Stable per-user hue for remote rulers. Hashes the userId to a hue so the
 * same peer keeps the same color across frames without any shared registry.
 */
export function rulerColorForUser(userId: string): string {
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 85%, 60%)`
}

/**
 * RulerOverlay — renders the ruler line + label as an absolutely-positioned
 * SVG/HTML overlay on top of the PIXI canvas.
 *
 * The ruler hook stores `start`/`end` in WORLD space (after inverse pan/zoom).
 * To render them on the screen-space DOM overlay, we re-apply the forward
 * viewport transform here (screen = world * scale + viewportPos). This keeps
 * the line glued to the world even as the user pans/zooms.
 */
export function RulerOverlay({
  start,
  end,
  gridSize,
  viewportScale,
  viewportX,
  viewportY,
  canvasWidth,
  canvasHeight,
  visible,
  color = 'hsl(45, 100%, 60%)',
  label: namePrefix,
  shape = 'line',
}: RulerOverlayProps) {
  if (!visible || !start || !end || gridSize <= 0) return null

  // World -> screen (inverse of the inverse transform applied at input time).
  // Scalars (px lengths) just multiply by the scale; points add the offset.
  const toScreen = (p: RulerCoord) => ({
    x: p.x * viewportScale + viewportX,
    y: p.y * viewportScale + viewportY,
  })
  const sx1 = start.x * viewportScale + viewportX
  const sy1 = start.y * viewportScale + viewportY
  const sx2 = end.x * viewportScale + viewportX
  const sy2 = end.y * viewportScale + viewportY

  // Compute the shape geometry (in world space) + its label. The shape
  // helpers are the same pure functions covered by the unit tests.
  let shapeText: string
  let svgShape: React.ReactNode
  // Default label anchor: near the drag endpoint.
  let labelX = sx2 + 12
  let labelY = sy2 + 12

  if (shape === 'circle') {
    const t = computeCircleTemplate(start, end, gridSize)
    const c = toScreen(t.center)
    const r = t.radius * viewportScale
    shapeText = t.label
    svgShape = (
      <>
        <circle cx={c.x} cy={c.y} r={r} fill={color} fillOpacity={0.12} stroke="black" strokeWidth={4} opacity={0.6} />
        <circle cx={c.x} cy={c.y} r={r} fill="none" stroke={color} strokeWidth={2} />
        <circle cx={c.x} cy={c.y} r={4} fill={color} stroke="black" strokeWidth={1} />
      </>
    )
    labelX = c.x + r + 8
    labelY = c.y + 8
  } else if (shape === 'square') {
    const t = computeSquareTemplate(start, end, gridSize)
    const pts = t.corners.map(toScreen)
    const ptsStr = pts.map((p) => `${p.x},${p.y}`).join(' ')
    const c = toScreen(t.center)
    shapeText = t.label
    svgShape = (
      <>
        <polygon points={ptsStr} fill={color} fillOpacity={0.12} stroke="black" strokeWidth={4} opacity={0.6} />
        <polygon points={ptsStr} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        <circle cx={c.x} cy={c.y} r={4} fill={color} stroke="black" strokeWidth={1} />
      </>
    )
    labelX = pts[1].x + 8
    labelY = pts[1].y - 8
  } else if (shape === 'cone') {
    const t = computeConeTemplate(start, end, gridSize)
    const pts = t.polygon.map(toScreen)
    const ptsStr = pts.map((p) => `${p.x},${p.y}`).join(' ')
    shapeText = t.label
    svgShape = (
      <>
        <polygon points={ptsStr} fill={color} fillOpacity={0.12} stroke="black" strokeWidth={4} opacity={0.6} />
        <polygon points={ptsStr} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        <circle cx={pts[0].x} cy={pts[0].y} r={4} fill={color} stroke="black" strokeWidth={1} />
      </>
    )
  } else {
    // line (default) — classic two-line ruler + endpoint markers.
    const { squares, feet } = computeRulerDistance(start, end, gridSize)
    shapeText = `${squares} sq · ${feet} ft`
    svgShape = (
      <>
        <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke="black" strokeWidth={4} strokeLinecap="round" opacity={0.6} />
        <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke={color} strokeWidth={2} strokeLinecap="round" />
        <circle cx={sx1} cy={sy1} r={4} fill={color} stroke="black" strokeWidth={1} />
        <circle cx={sx2} cy={sy2} r={4} fill={color} stroke="black" strokeWidth={1} />
      </>
    )
  }

  const label = namePrefix ? `${namePrefix}: ${shapeText}` : shapeText

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${canvasWidth}px`,
        height: `${canvasHeight}px`,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 10,
      }}
    >
      <svg
        width={canvasWidth}
        height={canvasHeight}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        {svgShape}
      </svg>
      <div
        style={{
          position: 'absolute',
          left: `${labelX}px`,
          top: `${labelY}px`,
          background: 'hsla(0, 0%, 0%, 0.75)',
          color,
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          border: `1px solid ${color}`,
        }}
      >
        {label}
      </div>
    </div>
  )
}
